import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import { getSenderGroupsForTriage, applySenderBulkAction, type TriageSenderGroup } from '@/api/triage';
import { getArchiveFolderPath } from '@/lib/archiveResolver';
import { markSenderAsArchiving, removeEmailsFromStore } from '@/stores/emailStateHelpers';
import { restoreAfterFailedAction } from '@/stores/emailCommands';
import type { Email } from '@/types/email';
import { useFolderStore } from '@/stores/folderStore';
import { useAccountStore } from '@/stores/accountStore';
import { useEmailStore } from '@/stores/emailStore';
import { useSenderGroupingStore } from '@/stores/senderGroupingStore';
import { mailEvents } from '@/events/mailEvents';
import { toast } from 'sonner';

export type TriageSortOption =
  | 'most-messages' // Most emails first
  | 'high-priority' // Highest avg priority score first
  | 'low-priority' // Lowest avg priority score first
  | 'most-unread' // Most unread first
  | 'newest' // Most recent message first
  | 'oldest' // Oldest message first
  | 'likely-archive'; // Newsletters, promotional, low engagement

export interface TriageContext {
  accountId: string;
  accountIds: string[]; // All accounts this sender appears in (for unified mode)
  folderId: string;
  senderEmail: string;
  contactId: string | null;
}

interface TriageState {
  // Active state
  isTriageMode: boolean;
  currentIndex: number;
  sortBy: TriageSortOption;

  // Sender groups
  groups: TriageSenderGroup[];
  isLoading: boolean;
  loadError: string | null;

  // Current sender context for keyboard actions
  currentContext: TriageContext | null;

  // Protected (pinned) email IDs - excluded from bulk actions
  protectedIds: Set<string>;

  // Processed senders (tracked by sender key)
  processedSenders: Set<string>;

  // Actions
  setTriageMode: (active: boolean) => void;
  setCurrentIndex: (index: number) => void;
  setCurrentContext: (context: TriageContext | null) => void;
  setProtectedIds: (ids: Set<string>) => void;
  nextSender: () => void;
  prevSender: () => void;
  setSortBy: (sort: TriageSortOption) => void;
  markSenderProcessed: (senderKey: string) => void;
  loadGroups: (accountId: string, folderId: string) => Promise<void>;
  removeSender: (senderKey: string) => void;
  archiveSender: () => void;
  deleteSender: () => void;
  restoreSenderAfterFailure: (
    senderKey: string,
    groupSnapshot: TriageSenderGroup | null,
    removedSnapshots: Array<{ id: string; email: Email | null }>,
    senderEmail: string,
    kind: 'archive' | 'delete'
  ) => void;
  reset: () => void;
}

export const useTriageStore = create<TriageState>()(
  conditionalDevtools(
    (set, get) => ({
      isTriageMode: false,
      currentIndex: 0,
      sortBy: 'most-messages',
      groups: [],
      isLoading: false,
      loadError: null,
      currentContext: null,
      protectedIds: new Set(),
      processedSenders: new Set(),

      setTriageMode: (active) => {
        // Capture whether this session actually processed anything BEFORE the
        // reset below wipes it — drives the single reconciliation reload below.
        const processedDuringSession = !active && get().processedSenders.size > 0;
        set({
          isTriageMode: active,
          currentIndex: 0,
          currentContext: null,
          protectedIds: new Set(),
          groups: active ? get().groups : [],
          loadError: active ? get().loadError : null,
          // A failed/aborted session must not invisibly filter those senders
          // out of the NEXT session (F19).
          processedSenders: new Set(),
        });
        if (active) {
          // E7: the triage overlay is a full-screen takeover, so the detail pane
          // it covers is meaningless. Clear the selection on ENTRY — otherwise a
          // triage removal (user-initiated, so it still auto-advances) would
          // background-select the next message and auto-mark-read mail the user
          // never saw, under the overlay, with a URL rewrite.
          useEmailStore.getState().clearSelection();
        }
        // Per-archive mailbox reloads are SUPPRESSED during triage (each one
        // re-fetched + re-projected + re-grouped an invisible list behind the
        // overlay, saturating the main thread and making each Archive lag more
        // the further you got — see archiveSender/deleteSender). Closing a
        // session in which senders were processed does ONE reconciliation
        // reload so the mailbox reflects server truth without the per-sender
        // reload storm.
        if (processedDuringSession) {
          const accountId = useAccountStore.getState().selectedAccountId;
          const folderId = useFolderStore.getState().selectedFolderId();
          if (accountId && folderId) {
            // Triage's own reconciliation supersedes any reload that was
            // deferred (queued) during the session — drop the queue, then run
            // ONE ownership-aware reload through the event (L1/E6, R12).
            useSenderGroupingStore.setState({ pendingMailboxReload: null });
            mailEvents.emit('mail:mailbox:reload', {
              accountId,
              folderId,
              options: { reset: true },
            });
          }
        } else if (!active) {
          // Nothing was processed, but a reconnect/resync reload may have been
          // deferred while triage owned the list — drain it now (R12).
          void useSenderGroupingStore.getState().flushPendingMailboxReload();
        }
      },

      setCurrentIndex: (index) =>
        set({ currentIndex: index }),

      setCurrentContext: (context) =>
        set({ currentContext: context }),

      setProtectedIds: (ids) =>
        set({ protectedIds: ids }),

      nextSender: () =>
        set((state) => {
          // Clamp at one-past-the-end (the "All done" position) of the
          // FILTERED list — the view hides processed senders, so clamping
          // against the raw groups let the index dig past "All done" (F18).
          const visibleCount = state.processedSenders.size > 0
            ? state.groups.filter((g) => !state.processedSenders.has(g.key)).length
            : state.groups.length;
          return {
            currentIndex: Math.min(state.currentIndex + 1, Math.max(visibleCount, 0)),
          };
        }),

      prevSender: () =>
        set((state) => ({ currentIndex: Math.max(0, state.currentIndex - 1) })),

      setSortBy: (sort) =>
        set({ sortBy: sort, currentIndex: 0 }),

      markSenderProcessed: (senderKey) =>
        set((state) => {
          const newProcessed = new Set(state.processedSenders);
          newProcessed.add(senderKey);
          return { processedSenders: newProcessed };
        }),

      loadGroups: async (accountId, folderId) => {
        set({ isLoading: true, loadError: null });
        try {
          const response = await getSenderGroupsForTriage(accountId, folderId);
          set({ groups: response.senderGroups, isLoading: false });
        } catch (error) {
          console.error('Failed to load sender groups for triage:', error);
          set({ loadError: 'Failed to load sender groups. Please try again.', isLoading: false });
          toast.error('Failed to load sender groups');
        }
      },

      removeSender: (senderKey) =>
        set((state) => ({ groups: state.groups.filter((g) => g.key !== senderKey) })),

      archiveSender: () => {
        const { currentContext, protectedIds } = get();
        if (!currentContext) return;

        const { accountIds, folderId, senderEmail } = currentContext;
        const excludeIds = protectedIds.size > 0 ? Array.from(protectedIds) : undefined;
        const { accountFolderCache } = useFolderStore.getState();

        // E3 (skip-before-remove, mirrors engine N5): resolve every account's
        // archive destination UP FRONT. Accounts with no archive folder are
        // excluded from BOTH the optimistic removal AND the dispatch — the old
        // code removed their mail optimistically, then silently skipped it in
        // dispatch, so it vanished for the 60s grace and resurfaced un-archived
        // with no error.
        const archiveByAccount = new Map<string, string>();
        const droppedAccountIds = new Set<string>();
        accountIds.forEach((acctId) => {
          const path = getArchiveFolderPath(acctId, accountFolderCache);
          if (path) {
            archiveByAccount.set(acctId, path);
          } else {
            droppedAccountIds.add(acctId);
          }
        });
        if (!archiveByAccount.size) {
          toast.error('No archive folder found — nothing archived');
          return;
        }
        if (droppedAccountIds.size > 0) {
          toast.warning(
            `${droppedAccountIds.size} account${droppedAccountIds.size === 1 ? ' has' : 's have'} no archive folder — those messages were left in place`
          );
        }

        const senderKey = senderEmail.trim().toLowerCase();

        // Mark sender as archiving so reloads filter them out — E4: exempt the
        // pinned ("kept") ids so they aren't suppressed and dropped from the
        // close reload's ingest filter.
        markSenderAsArchiving(senderEmail, excludeIds);

        // Optimistically remove sender from list and mark as processed
        get().markSenderProcessed(senderKey);
        get().removeSender(senderKey);

        // Remove the sender's non-protected emails IN ARCHIVABLE ACCOUNTS from
        // the store (E3) so sender groups, counts, and subject previews update
        // immediately. Snapshot first — the failure path restores them (N12).
        const emailState = useEmailStore.getState();
        const removedSnapshots = emailState.emails
          .filter(
            (e) =>
              e.from?.email?.trim().toLowerCase() === senderKey &&
              !protectedIds.has(e.id) &&
              !droppedAccountIds.has(e.accountId)
          )
          .map((e) => ({ id: e.id, email: e }));
        const groupSnapshot = get().groups.find((g) => g.key === senderKey) ?? null;
        if (removedSnapshots.length > 0) {
          // E7: EmailList is unmounted during triage but the pre-triage reader
          // selection survives underneath — a user-classified removal here
          // would auto-advance that hidden reader (background selectEmail, URL
          // rewrite, auto-mark-read). Selection-safe 'passive'; the grace
          // stamp (markRemoved) still hides the rows through the close reload.
          removeEmailsFromStore(removedSnapshots.map((entry) => entry.id), {
            initiator: 'passive',
          });
        }

        // Clear stale cached totals/prefetch for this sender so the remaining
        // pinned emails don't show the old count
        useSenderGroupingStore.getState().clearSenderFromCaches(senderKey);

        // Capture the mailbox the action was issued FROM — the completion
        // reload must not hard-reset whatever mailbox the user has navigated
        // to by the time the slow per-account calls finish.
        const actionAccountId = useAccountStore.getState().selectedAccountId;
        const actionFolderId = useFolderStore.getState().selectedFolderId();

        const archivableAccounts = Array.from(archiveByAccount.keys());

        // Run archive in background - process ALL archivable accounts
        (async () => {
          // E5: per-account reconciliation — allSettled, not all-or-nothing. A
          // partial failure restores ONLY the failed accounts' emails; the
          // genuinely-archived rows stay gone instead of flickering back.
          const settled = await Promise.allSettled(
            archivableAccounts.map((acctId) =>
              applySenderBulkAction(acctId, folderId, senderEmail, 'archive', archiveByAccount.get(acctId)!, excludeIds)
            )
          );
          const failedAccounts = new Set<string>();
          settled.forEach((result, i) => {
            if (result.status === 'rejected') {
              console.error('Failed to archive (triage):', result.reason);
              failedAccounts.add(archivableAccounts[i]);
            }
          });

          if (failedAccounts.size === archivableAccounts.length) {
            // Total failure — restore everything + put the card back (N12).
            get().restoreSenderAfterFailure(senderKey, groupSnapshot, removedSnapshots, senderEmail, 'archive');
            return;
          }

          if (failedAccounts.size > 0) {
            // Partial — restore only the failed accounts' emails; keep the card
            // gone and the other accounts archived.
            const failedSnapshots = removedSnapshots.filter(
              (s) => s.email && failedAccounts.has(s.email.accountId)
            );
            restoreAfterFailedAction(failedSnapshots, {
              senderEmail,
              message: `${failedAccounts.size} account${failedAccounts.size === 1 ? '' : 's'} failed to archive — restored`,
            });
          } else {
            const keptCount = excludeIds?.length ?? 0;
            toast.success(keptCount > 0 ? `Archived emails from sender (kept ${keptCount})` : 'Archived emails from sender');
          }

          // Refresh ONLY if the user is still on the action-time mailbox AND
          // has already left triage. During triage the optimistic removal
          // already updated the view; the reload is suppressed here (and one
          // reconciliation reload runs on close — see setTriageMode).
          const { selectedAccountId } = useAccountStore.getState();
          const currentFolderId = useFolderStore.getState().selectedFolderId();
          if (
            !get().isTriageMode &&
            actionAccountId &&
            actionFolderId &&
            selectedAccountId === actionAccountId &&
            currentFolderId === actionFolderId
          ) {
            mailEvents.emit('mail:mailbox:reload', {
              accountId: actionAccountId,
              folderId: actionFolderId,
              options: { reset: true },
            });
          }
        })();
      },

      deleteSender: () => {
        const { currentContext, protectedIds } = get();
        if (!currentContext) return;

        const { accountIds, folderId, senderEmail } = currentContext;
        const excludeIds = protectedIds.size > 0 ? Array.from(protectedIds) : undefined;

        const senderKey = senderEmail.trim().toLowerCase();

        // Mark sender as being deleted so reloads filter them out — E4: exempt
        // the pinned ("kept") ids so they aren't suppressed.
        markSenderAsArchiving(senderEmail, excludeIds);

        // Optimistically remove sender from list and mark as processed
        get().markSenderProcessed(senderKey);
        get().removeSender(senderKey);

        // Remove the sender's non-protected emails from the email store so
        // sender groups, counts, and subject previews update immediately.
        // Snapshot first — the failure path restores them (N12).
        const emailState = useEmailStore.getState();
        const removedSnapshots = emailState.emails
          .filter((e) => e.from?.email?.trim().toLowerCase() === senderKey && !protectedIds.has(e.id))
          .map((e) => ({ id: e.id, email: e }));
        const groupSnapshot = get().groups.find((g) => g.key === senderKey) ?? null;
        if (removedSnapshots.length > 0) {
          // E7: selection-safe against the hidden reader (see archiveSender).
          removeEmailsFromStore(removedSnapshots.map((entry) => entry.id), {
            initiator: 'passive',
          });
        }

        // Clear stale cached totals/prefetch for this sender so the remaining
        // pinned emails don't show the old count
        useSenderGroupingStore.getState().clearSenderFromCaches(senderKey);

        // Capture the action-time mailbox (see archiveSender).
        const actionAccountId = useAccountStore.getState().selectedAccountId;
        const actionFolderId = useFolderStore.getState().selectedFolderId();

        // Run delete in background - process ALL accounts for unified mode
        (async () => {
          // E5: per-account reconciliation — allSettled, not all-or-nothing.
          const settled = await Promise.allSettled(
            accountIds.map((acctId) =>
              applySenderBulkAction(acctId, folderId, senderEmail, 'delete', undefined, excludeIds)
            )
          );
          const failedAccounts = new Set<string>();
          settled.forEach((result, i) => {
            if (result.status === 'rejected') {
              console.error('Failed to delete (triage):', result.reason);
              failedAccounts.add(accountIds[i]);
            }
          });

          if (failedAccounts.size === accountIds.length) {
            // Total failure — restore everything + put the card back (N12).
            get().restoreSenderAfterFailure(senderKey, groupSnapshot, removedSnapshots, senderEmail, 'delete');
            return;
          }

          if (failedAccounts.size > 0) {
            // Partial — restore only the failed accounts' emails.
            const failedSnapshots = removedSnapshots.filter(
              (s) => s.email && failedAccounts.has(s.email.accountId)
            );
            restoreAfterFailedAction(failedSnapshots, {
              senderEmail,
              message: `${failedAccounts.size} account${failedAccounts.size === 1 ? '' : 's'} failed to delete — restored`,
            });
          } else {
            const keptCount = excludeIds?.length ?? 0;
            toast.success(keptCount > 0 ? `Deleted emails from sender (kept ${keptCount})` : 'Deleted emails from sender');
          }

          // Refresh ONLY if the user is still on the action-time mailbox AND
          // has already left triage (see archiveSender).
          const { selectedAccountId } = useAccountStore.getState();
          const currentFolderId = useFolderStore.getState().selectedFolderId();
          if (
            !get().isTriageMode &&
            actionAccountId &&
            actionFolderId &&
            selectedAccountId === actionAccountId &&
            currentFolderId === actionFolderId
          ) {
            mailEvents.emit('mail:mailbox:reload', {
              accountId: actionAccountId,
              folderId: actionFolderId,
              options: { reset: true },
            });
          }
        })();
      },

      // Engine-standard failure restore (N12): clear the sender-archiving mark
      // and the removed ids' 60s grace, re-merge the snapshots, and put the
      // sender card back so the user can retry. The old failure path was a
      // console line + toast — emails stayed suppressed and the sender mark
      // filtered every reload until a refresh.
      restoreSenderAfterFailure: (senderKey, groupSnapshot, removedSnapshots, senderEmail, kind) => {
        restoreAfterFailedAction(removedSnapshots, {
          senderEmail,
          message: `Failed to ${kind} emails from this sender — restored`,
        });
        set((state) => {
          const processed = new Set(state.processedSenders);
          processed.delete(senderKey);
          const groups = groupSnapshot && !state.groups.some((g) => g.key === senderKey)
            ? [groupSnapshot, ...state.groups]
            : state.groups;
          return { processedSenders: processed, groups };
        });
      },

      reset: () =>
        set({
          isTriageMode: false,
          currentIndex: 0,
          sortBy: 'most-messages',
          groups: [],
          isLoading: false,
          loadError: null,
          currentContext: null,
          protectedIds: new Set(),
          processedSenders: new Set(),
        }),
    }),
    { name: 'TriageStore' }
  )
);
